<template>
  <div>
    <div class="mb-4 flex justify-between gap-2 max-sm:flex-col">
      <div class="flex items-center gap-2">
        <CVButton
          v-if="showBackButton"
          size="icon"
          variant="ghost"
          :disabled="selectingCarId !== null"
          @click="emit('back')"
          ><ArrowLeftIcon class="size-4"
        /></CVButton>
        <h2>{{ t('selectCarTitle') }}</h2>
      </div>
      <StationSwitcher v-model="currentStationId" />
    </div>
    <div v-if="isPending" class="flex items-center justify-center py-5">
      <Spinner />
    </div>
    <div v-else-if="offers.length > 0" class="space-y-4">
      <OfferComp
        v-for="offer in offers"
        :key="offer.car.id"
        :offer="offer"
        :hide-price="hidePrices"
        :show-transfer-breakdown="showTransferBreakdown"
        :is-selecting="selectingCarId === offer.car.id"
        :disabled="selectingCarId !== null"
        @on-offer-select="handleSelect"
      />
    </div>
    <EntityListExceptions
      v-else
      :status="status"
      :error="error"
      :items-count="offers.length"
      :no-items-text="t('noCarsFound')"
    />
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import OfferComp from './Offer.vue';
import StationSwitcher from '@/views/settings/components/StationSwitcher.vue';
import { useOffers } from '@/queries/use-offers.ts';
import { CarStatus } from '@/entities/cars/car-status.enum.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data.ts';
import { GetOffersSort, TaxProcedure, type Offer } from '@carvia/ros-client-types';
import Spinner from '@/components/icons/Spinner.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { ArrowLeftIcon } from 'lucide-vue-next';
import EntityListExceptions from '@/components/EntityListExceptions.vue';

const props = defineProps<{
  startDate: Date | string;
  endDate: Date | string;
  transferDeliveryLocation?: [number, number];
  transferReturnLocation?: [number, number];
  bookingCreatedDate?: Date | string;
  initialStationId: string;
  showBackButton?: boolean;
  partnerId?: string;
  ignoreOccupationOfCarId?: string;
  hidePrices?: boolean;
  insuranceCaseId?: string;
  showTransferBreakdown?: boolean;
  taxProcedure?: TaxProcedure;
  country?: string;
  selectOffer: (offer: Offer) => void | Promise<void>;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
}>();

const { t } = useI18n();

const currentStationId = ref(props.initialStationId);
const selectingCarId = ref<string | null>(null);

const {
  data: offersData,
  isPending,
  status,
  error,
} = useOffers(
  computed(() => ({
    limit: 10000,
    startDate: new Date(props.startDate),
    endDate: new Date(props.endDate),
    transferDeliveryLocation: props.transferDeliveryLocation,
    transferReturnLocation: props.transferReturnLocation,
    bookingCreatedDate: props.bookingCreatedDate ? new Date(props.bookingCreatedDate) : undefined,
    stationIds: [currentStationId.value],
    isGhostCar: false,
    ignoreOpeningHoursAndHolidays: true,
    status: [CarStatus.ORDERED, CarStatus.FLOAT_IN],
    isAvailable: true,
    includeInternalStations: true,
    sort: [GetOffersSort.MAKE, GetOffersSort.TOTAL_PRICE],
    reduceCarsToOnePerVehicleType: false,
    partnerId: props.partnerId,
    ignoreOccupationOfCarId: props.ignoreOccupationOfCarId,
    insuranceCaseId: props.insuranceCaseId,
    ...(props.taxProcedure ? { taxProcedure: props.taxProcedure } : {}),
    ...(props.country ? { country: props.country } : {}),
  })),
  computed(() => selectingCarId.value === null),
);
const offers = useFlattenPaginatedData(offersData);

const handleSelect = async (offer: Offer) => {
  selectingCarId.value = offer.car.id;
  try {
    await props.selectOffer(offer);
  } finally {
    selectingCarId.value = null;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "selectCarTitle": "Select car",
    "select": "select",
    "changeCar": "Change car?",
    "noCarsFound": "No cars found"
  },
  "de": {
    "selectCarTitle": "Auto wählen",
    "select": "auswählen",
    "changeCar": "Auto ändern?",
    "noCarsFound": "Keine Autos gefunden"
  },
  "es": {
    "selectCarTitle": "Elegir vehículo",
    "select": "elegir",
    "changeCar": "¿Cambiar de vehículo?",
    "noCarsFound": "No se encontraron coches"
  }
}
</i18n>
